import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Room, RoomEvent, Track } from 'livekit-client';
import { LivekitService } from '../../services/livekit.service';
import { EchoService } from '../../services/echo.service';
import { environment } from '../../../environments/environment';

interface HostChatMsg { name: string; text: string; mine: boolean; }

@Component({
  selector: 'app-go-live',
  templateUrl: './go-live.component.html',
  styleUrls: ['./go-live.component.css']
})
export class GoLiveComponent implements OnDestroy {
  @ViewChild('localVideo') localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('rtmpVideo') rtmpVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('chatBox') chatBoxRef?: ElementRef<HTMLDivElement>;
  isLive = false;
  starting = false;
  error = '';
  viewers = 0;
  streamToken = '';
  canShare = !!(navigator as any).share;

  // OBS / RTMP mode
  isRtmp = false;
  rtmpUrl = '';
  rtmpKey = '';
  rtmpConnected = false;   // OBS se stream aa rahi hai?
  showKey = false;

  // live chat (LiveKit data channel)
  chat: HostChatMsg[] = [];
  newMsg = '';
  private room?: Room;

  constructor(
    private livekitService: LivekitService,
    private echoService: EchoService,
    private http: HttpClient
  ) {}

  get viewerLink(): string {
    return `${window.location.origin}/live/${this.streamToken}`;
  }

  get whatsappLink(): string {
    return 'https://wa.me/?text=' + encodeURIComponent('Watch my live stream: ' + this.viewerLink);
  }

  get facebookLink(): string {
    return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(this.viewerLink);
  }

  goLiveClicked() {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      alert('Login required');
      return;
    }
    this.goLive(userId);
  }

  goLiveRtmpClicked() {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      alert('Login required');
      return;
    }
    this.goLiveRtmp(userId);
  }

  /* ---------- OBS / RTMP ---------- */
  async goLiveRtmp(userId: string) {
    this.starting = true;
    this.error = '';
    try {
      // 1) OBS ka server URL + stream key + viewer link ka token
      const res: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}live/rtmp/ingress`, {}, { headers: this.echoService.headers() })
      );
      this.rtmpUrl = res.server_url;
      this.rtmpKey = res.stream_key;
      this.streamToken = res.viewer_token;

      // 2) host room mein sirf chat + preview ke liye aata hai (camera/mic on nahi hota)
      const tk = await this.livekitService.getToken(`user_${userId}_live`, 'broadcaster');
      const room = new Room();
      this.room = room;
      this.chat = [];
      this.rtmpConnected = false;

      room.on(RoomEvent.ParticipantConnected, () => this.refreshRtmp());
      room.on(RoomEvent.ParticipantDisconnected, () => this.refreshRtmp());
      room.on(RoomEvent.TrackSubscribed, (track: Track) => this.attachPreview(track));
      room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: any, _kind?: any, topic?: string) =>
        this.onData(payload, participant, topic));

      await room.connect(tk.server_url, tk.token);
      this.isRtmp = true;
      this.isLive = true;
      room.remoteParticipants.forEach(p =>
        p.trackPublications.forEach(pub => { if (pub.track) { this.attachPreview(pub.track); } })
      );
      this.refreshRtmp();
    } catch (e: any) {
      this.room?.disconnect();
      this.room = undefined;
      this.error = e?.error?.message || 'Could not prepare the OBS stream. Please try again.';
    } finally {
      this.starting = false;
    }
  }

  /** OBS ka participant ('rtmp_<id>') room mein hai ya nahi + viewers count */
  private refreshRtmp() {
    if (!this.room) { return; }
    const all = Array.from(this.room.remoteParticipants.values());
    this.rtmpConnected = all.some(p => p.identity.startsWith('rtmp_'));
    this.viewers = all.filter(p => !p.identity.startsWith('rtmp_')).length;
  }

  /** host ko preview: bilkul wahi jo viewers dekhte hain (preview muted, awaaz nahi) */
  private attachPreview(track: Track) {
    if (track.kind === Track.Kind.Video && this.rtmpVideoRef) {
      track.attach(this.rtmpVideoRef.nativeElement);
    }
  }

  copyText(value: string, label: string) {
    navigator.clipboard.writeText(value);
    alert(label + ' copied!');
  }

  async goLive(userId: string) {
    this.starting = true;
    this.error = '';
    try {
      const room = await this.livekitService.joinRoom(`user_${userId}_live`, 'broadcaster');
      this.room = room;
      this.chat = [];

      // server par live row + viewer token
      const res: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}live/start`, {}, { headers: this.echoService.headers() })
      );
      this.streamToken = res.token;
      this.isRtmp = false;
      this.isLive = true;

      const localVideoTrack = room.localParticipant.videoTrackPublications.values().next().value?.videoTrack;
      if (localVideoTrack) {
        localVideoTrack.attach(this.localVideoRef.nativeElement);
      }

      const update = () => (this.viewers = room.remoteParticipants.size);
      room.on(RoomEvent.ParticipantConnected, update);
      room.on(RoomEvent.ParticipantDisconnected, update);
      room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: any, _kind?: any, topic?: string) =>
        this.onData(payload, participant, topic));
      update();
    } catch (e) {
      this.livekitService.leaveRoom();
      this.room = undefined;
      this.error = 'Could not start the live stream. Please check your camera and mic permissions and try again.';
    } finally {
      this.starting = false;
    }
  }

  stopLive() {
    if (this.isRtmp) {
      // OBS mode: sirf is page ka room connection band hota hai.
      // Live khatam karne ke liye OBS mein "Stop Streaming" dabana hota hai (webhook row ko ended kar deta hai).
      this.room?.disconnect();
    } else {
      this.livekitService.leaveRoom();
      this.http.post(`${environment.apiUrl}live/stop`, {}, { headers: this.echoService.headers() })
        .subscribe({ error: () => {} });
    }
    this.room = undefined;
    this.isLive = false;
    this.isRtmp = false;
    this.rtmpConnected = false;
    this.rtmpUrl = '';
    this.rtmpKey = '';
    this.showKey = false;
    this.viewers = 0;
    this.streamToken = '';
    this.chat = [];
    this.newMsg = '';
  }

  /* ---------- chat ---------- */
  sendChat() {
    const text = this.newMsg.trim();
    if (!text || !this.room) { return; }
    const name = this.room.localParticipant.name || 'Host';

    this.room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify({ name, text })), { reliable: true, topic: 'chat' })
      .then(() => this.pushChat({ name, text, mine: true }))
      .catch(() => {});
    this.newMsg = '';
  }

  private onData(payload: Uint8Array, participant?: any, topic?: string) {
    if (topic && topic !== 'chat') { return; }
    try {
      const m = JSON.parse(new TextDecoder().decode(payload));
      if (!m || typeof m.text !== 'string') { return; }
      this.pushChat({
        name: String(m.name || participant?.name || 'Viewer').slice(0, 60),
        text: m.text.slice(0, 500),
        mine: false
      });
    } catch { /* ghalat packet ignore */ }
  }

  private pushChat(m: HostChatMsg) {
    this.chat = [...this.chat, m].slice(-200);
    setTimeout(() => {
      const el = this.chatBoxRef?.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    }, 30);
  }

  copyViewerLink() {
    navigator.clipboard.writeText(this.viewerLink);
    alert('Viewer link copied! Share it with your audience.');
  }

  async shareLive() {
    try {
      await (navigator as any).share({
        title: 'Live Stream',
        text: 'Watch my live stream!',
        url: this.viewerLink
      });
    } catch {
      // user cancelled sharing
    }
  }

  ngOnDestroy() { if (this.isLive) this.stopLive(); }
}